<template>
  <div v-if="visible" class="tree-selector-overlay" @click.self="$emit('close')">
    <div class="tree-selector-dialog">
      <div class="tree-selector-header">
        <h3>{{ title }}</h3>
        <button @click="$emit('close')" class="tree-selector-close">&times;</button>
      </div>
      <div class="tree-selector-body">
        <div class="tree-selector-search">
          <i class="fas fa-search"></i>
          <input
            v-model="filterText"
            type="text"
            :placeholder="searchPlaceholder"
            class="tree-selector-search-input"
          >
        </div>
        <div class="tree-selector-list">
          <template v-for="node in filteredNodes" :key="node.id">
            <TreeNode
              :node="node"
              :depth="0"
              :icon="icon"
              :selected-ids="selectedIds"
              :expanded-ids="expandedIds"
              :multiple="multiple"
              @select="handleSelect"
              @toggle-expand="toggleExpand"
            />
          </template>
          <div v-if="filteredNodes.length === 0" class="tree-selector-empty">
            {{ filterText ? '未找到匹配项' : '暂无数据' }}
          </div>
        </div>
      </div>
      <div v-if="multiple" class="tree-selector-footer">
        <button @click="$emit('close')" class="ts-btn ts-btn-cancel">取消</button>
        <button @click="$emit('confirm')" class="ts-btn ts-btn-confirm">
          <i class="fas fa-check"></i> 确认 ({{ selectedIds.length }})
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, h, watch } from 'vue'

const props = defineProps({
  visible: { type: Boolean, default: false },
  title: { type: String, default: '选择' },
  searchPlaceholder: { type: String, default: '搜索...' },
  icon: { type: String, default: 'fa-building' },
  nodes: { type: Array, default: () => [] },
  selectedIds: { type: Array, default: () => [] },
  multiple: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'confirm', 'update:selectedIds'])

const filterText = ref('')
const expandedIds = ref([])

// 收集所有节点 ID
const collectAllIds = (nodes) => {
  const ids = []
  for (const n of nodes) {
    ids.push(n.id)
    if (n.children && n.children.length > 0) ids.push(...collectAllIds(n.children))
  }
  return ids
}

// 数据变化时自动展开全部节点
watch(() => props.nodes, (val) => {
  expandedIds.value = collectAllIds(val || [])
}, { immediate: true, deep: true })

// 打开弹窗时如有搜索词则也收起（不清空 expandedIds 可确保默认全展开）
watch(() => props.visible, (v) => {
  if (v) {
    filterText.value = ''
    expandedIds.value = collectAllIds(props.nodes || [])
  }
})

// 递归过滤树节点
const filterTree = (nodes, keyword) => {
  if (!keyword) return nodes
  const kw = keyword.toLowerCase()
  const result = []
  for (const node of nodes) {
    const nameMatch = (node.name || '').toLowerCase().includes(kw)
    const childrenMatch = node.children && node.children.length > 0
      ? filterTree(node.children, kw)
      : null
    if (nameMatch || (childrenMatch && childrenMatch.length > 0)) {
      result.push({
        ...node,
        children: childrenMatch && childrenMatch.length > 0 ? childrenMatch : node.children
      })
    }
  }
  return result
}

const filteredNodes = computed(() => filterTree(props.nodes, filterText.value))

function toggleExpand(id) {
  const idx = expandedIds.value.indexOf(id)
  if (idx !== -1) expandedIds.value.splice(idx, 1)
  else expandedIds.value.push(id)
}

function handleSelect(node) {
  if (props.multiple) {
    const ids = [...props.selectedIds]
    const idx = ids.indexOf(node.id)
    if (idx !== -1) ids.splice(idx, 1)
    else ids.push(node.id)
    emit('update:selectedIds', ids)
  } else {
    emit('update:selectedIds', [node.id])
    emit('confirm')
  }
}

// 递归树节点渲染组件
const TreeNode = {
  name: 'TreeNode',
  props: ['node', 'depth', 'icon', 'selectedIds', 'expandedIds', 'multiple'],
  emits: ['select', 'toggleExpand'],
  setup(p, { emit: nodeEmit }) {
    return () => {
      const { node, depth, icon, selectedIds, expandedIds } = p
      const hasChildren = node.children && node.children.length > 0
      const isExpanded = expandedIds.includes(node.id)
      const isSelected = selectedIds.includes(node.id)

      const children = []

      children.push(
        h('div', {
          class: ['tree-selector-item', { selected: isSelected }],
          style: { paddingLeft: (depth * 20 + 12) + 'px' },
          onClick: () => nodeEmit('select', node)
        }, [
          h('span', {
            class: ['tree-selector-expand', { expanded: hasChildren && isExpanded }],
            onClick: hasChildren ? (e) => { e.stopPropagation(); nodeEmit('toggleExpand', node.id) } : undefined
          }, hasChildren ? [h('i', { class: 'fas fa-caret-right' })] : undefined),
          h('i', { class: ['fas', icon, 'tree-selector-icon'] }),
          h('span', { class: 'tree-selector-name' }, node.name),
          p.multiple ? h('span', { class: ['tree-selector-check', { checked: isSelected }] }, [
            h('i', { class: isSelected ? 'fas fa-check-square' : 'far fa-square' })
          ]) : null
        ])
      )

      if (hasChildren && isExpanded) {
        node.children.forEach(child => {
          children.push(h(TreeNode, {
            key: child.id,
            node: child,
            depth: depth + 1,
            icon: icon,
            selectedIds: selectedIds,
            expandedIds: expandedIds,
            multiple: p.multiple,
            onSelect: (n) => nodeEmit('select', n),
            onToggleExpand: (id) => nodeEmit('toggleExpand', id)
          }))
        })
      }

      return children
    }
  }
}
</script>

<style>
.tree-selector-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}
.tree-selector-dialog {
  background: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 480px;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.tree-selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #e8ecf1;
}
.tree-selector-header h3 { margin: 0; font-size: 15px; font-weight: 600; color: #1f2937; }
.tree-selector-close {
  background: none; border: none; font-size: 20px; color: #9ca3af;
  cursor: pointer; line-height: 1;
}
.tree-selector-close:hover { color: #374151; }
.tree-selector-body { flex: 1; overflow: hidden; display: flex; flex-direction: column; }
.tree-selector-search {
  position: relative;
  margin: 12px;
  flex-shrink: 0;
}
.tree-selector-search i {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  color: #9ca3af; font-size: 12px;
}
.tree-selector-search-input {
  width: 100%;
  padding: 8px 10px 8px 30px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
}
.tree-selector-search-input:focus { border-color: #1ab394; }
.tree-selector-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 12px;
  min-height: 120px;
  max-height: 360px;
}
.tree-selector-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s;
  user-select: none;
}
.tree-selector-item:hover { background: #f3f4f6; }
.tree-selector-item.selected { background: #ecfdf5; color: #059669; }
.tree-selector-expand {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; flex-shrink: 0;
  transition: transform 0.2s;
  color: #9ca3af; font-size: 11px;
}
.tree-selector-expand.expanded { transform: rotate(90deg); }
.tree-selector-icon { font-size: 13px; color: #10b981; width: 16px; text-align: center; flex-shrink: 0; }
.tree-selector-name { font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tree-selector-check { margin-left: auto; font-size: 14px; color: #d1d5db; flex-shrink: 0; }
.tree-selector-check.checked { color: #1ab394; }
.tree-selector-empty { text-align: center; padding: 30px 0; color: #9ca3af; font-size: 13px; }
.tree-selector-footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 12px 18px; border-top: 1px solid #e8ecf1;
}

/* 按钮样式 — 与现有弹窗 .btn 保持一致 */
.ts-btn {
  padding: 6px 15px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;
}
.ts-btn-cancel {
  background-color: #f3f3f4;
  color: #676a6c;
  border: 1px solid #e7eaec;
}
.ts-btn-cancel:hover {
  background-color: #e5e6e7;
}
.ts-btn-confirm {
  background-color: #1ab394;
  color: #fff;
}
.ts-btn-confirm:hover {
  background-color: #18a689;
}
</style>
